import {
  invariant,
  useBoolean,
  useOnMount,
  useOnSuccessQuery,
  UseQueryResult,
} from '@wyz/utils';
import { isNil } from 'lodash-es';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { usePurchaseContext } from '~/purchase/context';
import { IBaseSearchFormSchema } from '~/purchase/types';

interface UseSearchResultOptions<TSearchQuery, TSort> {
  hasMinimalParams: (values: TSearchQuery) => boolean;
  getValues: () => TSearchQuery;
  onParamsValid: (values: TSearchQuery) => void;
  filterQuery: UseQueryResult<unknown[], unknown>;
  dataUpdatedAt?: number;
  onSubmit: (
    payload: Partial<{
      searchQuery: IBaseSearchFormSchema;
      sort?: TSort;
    }>,
  ) => void;
}

export function useSearchResult<TSearchQuery, TSort>({
  hasMinimalParams,
  getValues,
  onParamsValid,
  filterQuery,
  dataUpdatedAt,
  onSubmit,
}: UseSearchResultOptions<TSearchQuery, TSort>) {
  const navigate = useNavigate();
  const { state } = usePurchaseContext();

  const targetCustomer = state.targetCustomer;
  invariant(targetCustomer);

  const resultRef = React.useRef<HTMLSpanElement>(null);
  const scrolledToResult = React.useRef<boolean>(false);
  const filteredLastUpdated = React.useRef<number | undefined>(undefined);
  const [isModalOpen, { setFalse: closeModal, setTrue: openModal }] =
    useBoolean(false);

  useOnMount(() => {
    const currentValues = getValues();
    if (hasMinimalParams(currentValues)) {
      onParamsValid(currentValues);
    } else {
      void navigate('..');
    }
  });

  useOnSuccessQuery(filterQuery, () => {
    if (dataUpdatedAt && filteredLastUpdated.current === dataUpdatedAt) {
      return;
    }

    if (dataUpdatedAt) {
      filteredLastUpdated.current = dataUpdatedAt;
    }

    setTimeout(() => {
      if (
        !scrolledToResult.current &&
        resultRef.current &&
        !isNil(filterQuery.data) &&
        filterQuery.data.length > 0
      ) {
        scrolledToResult.current = true;
        resultRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });

  const handleOnSubmit = React.useCallback(
    (payload: Partial<{ searchQuery: IBaseSearchFormSchema }>) => {
      onSubmit(payload);
      closeModal();
    },
    [onSubmit, closeModal],
  );

  const handleOnSort = (sort: TSort | undefined) => {
    onSubmit({ sort });
  };
  return {
    resultRef,
    isModalOpen,
    openModal,
    closeModal,
    onSubmit: handleOnSubmit,
    onSort: handleOnSort,
  };
}
